// AuthEventTracker.jsx
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { trackEvent } from "../lib/mixpanel";

const AUTH_METHODS = new Set(["google", "microsoft", "apple"]);

/**
 * Fires login_completed / account_created when the backend's OAuth callback
 * lands the user back in the app.
 *
 * The backend appends `?auth_event=login|signup&auth_method=<provider>` to its
 * post-login redirect (controllers/auth.controller.js → withAuthOutcome). That
 * works for every landing page — the page the user came from, /settings, and
 * the desktop handoff at /signup-successful — so there is one place to track
 * instead of one per route. The params are stripped afterwards so a refresh
 * can't fire the event twice.
 *
 * Waits for AuthContext to finish loading so the event lands on the identified
 * user rather than the anonymous device id.
 */
function AuthEventTracker() {
  const location = useLocation();
  const navigate = useNavigate();
  const { loading } = useAuth();

  useEffect(() => {
    if (loading) return;

    const params = new URLSearchParams(location.search);
    const authEvent = params.get("auth_event");
    const method = String(params.get("auth_method") || "").toLowerCase();
    if (!authEvent) return;

    const loginMethod = AUTH_METHODS.has(method) ? method : "unknown";
    // Every successful OAuth round-trip establishes a session → login_completed,
    // so login_method_clicked → login_completed is a complete funnel for new and
    // returning users alike. A new account additionally records account_created.
    trackEvent("login_completed", { login_method: loginMethod });
    if (authEvent === "signup") {
      trackEvent("account_created", {
        signup_method: loginMethod,
        is_new_user: true,
      });
    }

    params.delete("auth_event");
    params.delete("auth_method");
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash },
      { replace: true, state: location.state }
    );
  }, [loading, location, navigate]);

  return null;
}

export default AuthEventTracker;
